import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Grid, TextField, Box, Chip } from '@mui/material';
import { debounce } from 'lodash';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

import PagedTable from 'components/table/PagedTable';
import { filterSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';

import { PUBLIC_COMPANY_LIST_QUERY } from './gql';

import { ErrorAlert, RedirectError } from 'pages/error';

/**
 * Companies list page with pagination and debounced search functionality.
 * Uses network-only fetch policy to ensure fresh data.
 */
export default function ClientsCompaniesList() {
  const navigate = useNavigate();
  const filter = filterSelector();
  const user = userSelector()!;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [query, { data, loading, error }] = useLazyQuery(PUBLIC_COMPANY_LIST_QUERY, {
    // Always hit network on filter changes to avoid stale cache
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Debounce search term
  const debouncedUpdate = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedUpdate(searchTerm);
  }, [searchTerm, debouncedUpdate]);

  if (error) return <RedirectError err={error} />;

  return (
    <>
      <Helmet>
        <title>{i18next.t('clients-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PagedTable
            filter={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon />
                <TextField
                  label={i18next.t('search')}
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={String(i18next.t('clients-search-placeholder'))}
                  sx={{
                    minWidth: 300,
                    '& .MuiInputBase-input': { py: 0.5 },
                  }}
                />
              </Box>
            }
            filterKey={debouncedSearchTerm}
            refetchPage={(skip, take) =>
              query({
                variables: {
                  where: debouncedSearchTerm ? {
                    search: debouncedSearchTerm
                  } : undefined,
                  skip,
                  take,
                },
              })
            }
            rowsLoading={loading}
            headers={[
              i18next.t('publiccompany-list-header-name'),
              i18next.t('publiccompany-list-header-status'),
              i18next.t('publiccompany-list-header-customer-type'),
              i18next.t('publiccompany-list-header-id'),
              i18next.t('publiccompany-list-header-address'),
              i18next.t('publiccompany-list-header-email'),
              i18next.t('publiccompany-list-header-contacts-count'),
            ]}
            totalCount={data?.listPublicCompanies?.totalCount}
            rows={(data?.listPublicCompanies?.companies || []).map((row) => [
              <Link to={`/clients/companies/${row.id}`}>{row.name}</Link>,
              <Chip
                label={String(i18next.t(row.isActive ? 'publiccompany-status-active' : 'publiccompany-status-inactive'))}
                color={row.isActive ? 'success' : 'error'}
                size="small"
              />,
              row.isPremium ? 'Premium' : 'Classic',
              row.id,
              `${row.address || ''}, ${row.zip || ''} ${row.city || ''}`.trim(),
              row.email || '-',
              row.clientsCount || 0,
            ])}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/clients/companies/new')}
          >
            {i18next.t('publiccompany-button-new')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
}
